const STORAGE_KEY = "selectedProviderProfile";

export const readStoredProfile = () => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error("Error retrieving selected profile:", error);
    return null;
  }
};

export const positionedRows = (rows) => (rows ?? []).filter((row) => row.org_position_key != null);

export const showProfileControl = (rows) => {
  const positioned = positionedRows(rows);
  return positioned.length > 1 && positioned.every((row) => row.provider_id && row.position_name);
};

export const profileLabel = (row) => `${row.provider_id} / ${row.position_name}`;

export const pickProfile = (rows) => {
  const positioned = positionedRows(rows);
  const stored = readStoredProfile();
  return (
    positioned.find((row) => row.org_position_key === stored?.org_position_key) ?? positioned[0] ?? rows?.[0] ?? null
  );
};

const POSITION_FILTER_KEY = "payRequestPositionFilter";

// sentinel option of the Position filter; an empty key list means All Positions
export const ALL_POSITIONS_OPTION = { text: "All Positions", id: "ALL" };

export const positionOptions = (rows) =>
  positionedRows(rows).map((row) => ({
    text: profileLabel(row),
    id: row.org_position_key,
    provider_mstr_key: row.provider_mstr_key,
  }));

// the keys that are still among the user's positions, in profile order; anything else is dropped
export const knownPositionKeys = (rows, keys) => {
  const wanted = (keys ?? []).map(String);
  return positionedRows(rows)
    .map((row) => row.org_position_key)
    .filter((key) => wanted.includes(String(key)));
};

const distinctProviderIds = (rows) => [
  ...new Set((rows ?? []).map((row) => row.provider_mstr_key).filter((id) => id != null)),
];

// body params for the selected positions: none when the control is hidden; All Positions sends only the providers that have a positioned row
export const scopeParams = (rows, selectedKeys) => {
  if (!showProfileControl(rows)) return {};
  const keys = knownPositionKeys(rows, selectedKeys);
  if (keys.length === 0) {
    return { in_provider_ids: distinctProviderIds(positionedRows(rows)).join(",") };
  }
  const selectedRows = positionedRows(rows).filter((row) => keys.includes(row.org_position_key));
  return {
    in_provider_ids: distinctProviderIds(selectedRows).join(","),
    in_org_position_keys: keys.join(","),
  };
};

// the Position filter keys that match body params built by scopeParams; no lists is single mode, every position of providerId
export const keysForScope = (rows, scope, providerId) => {
  if (scope?.in_org_position_keys) {
    return knownPositionKeys(rows, String(scope.in_org_position_keys).split(","));
  }
  if (scope?.in_provider_ids) return [];
  const positioned = positionedRows(rows);
  const keys = positioned
    .filter((row) => String(row.provider_mstr_key) === String(providerId))
    .map((row) => row.org_position_key);
  return keys.length === positioned.length ? [] : keys;
};

export const readPositionFilter = () => {
  try {
    const stored = JSON.parse(sessionStorage.getItem(POSITION_FILTER_KEY));
    return Array.isArray(stored) ? stored : [];
  } catch (error) {
    console.error("Error retrieving position filter:", error);
    return [];
  }
};

export const storePositionFilter = (keys) => {
  try {
    sessionStorage.setItem(POSITION_FILTER_KEY, JSON.stringify(keys ?? []));
  } catch (error) {
    console.error("Error storing position filter:", error);
  }
};

// All Positions is exclusive: picking a position drops it, picking it clears the positions, and an empty pick reverts to it
export const applyAllPositionsSentinel = (prevItems, nextItems) => {
  const isSentinel = (item) => item?.id === ALL_POSITIONS_OPTION.id;
  const next = nextItems ?? [];
  let items = next;
  if (next.some(isSentinel) && next.length > 1) {
    items = (prevItems ?? []).some(isSentinel) ? next.filter((item) => !isSentinel(item)) : [ALL_POSITIONS_OPTION];
  }
  if (items.length === 0) {
    items = [ALL_POSITIONS_OPTION];
  }
  const keys = items.some(isSentinel) ? [] : items.map((item) => item.id);
  return { items, keys };
};
